// utils/anthropometry/formulas.js

const calculateSkinfoldsSum = (skinfolds, equation) => {
  const {
    triceps = 0,
    suprailiac = 0,
    subscapular = 0,
    chest = 0,
    axillary = 0,
    abdominal = 0,
    thigh = 0,
    calf = 0,
    biceps = 0
  } = skinfolds;

  let sum = 0;

  switch (equation) {
    case '1': // Jackson & Pollock - 3 Skinfolds
      sum = Number(triceps) + Number(suprailiac) + Number(thigh);
      break;
    case '2': // Jackson & Pollock - 7 Skinfolds
      sum = Number(triceps) + Number(subscapular) + Number(chest) +
        Number(axillary) + Number(abdominal) + Number(thigh) + Number(calf);
      break;
    case '3': // Petrosky - 4 Skinfolds
      sum = Number(triceps) + Number(suprailiac) + Number(subscapular) + Number(thigh);
      break;
    case '4': // Durnin & Womersley - 4 Skinfolds
      sum = Number(triceps) + Number(biceps) + Number(subscapular) + Number(suprailiac);
      break;
    default:
      return 0;
  }

  if (isNaN(sum)) return 0;
  return Number(sum).toFixed(2);
};

const calculateBodyFatPercentage = (gender, skinfoldsSum, age, equation) => {
  let density = 0;

  switch (equation) {
    case '1':
      density = gender === 'male'
        ? 1.10938 - (0.0008267 * skinfoldsSum) + (0.0000016 * skinfoldsSum ** 2) - (0.0002574 * age)
        : 1.0994921 - (0.0009929 * skinfoldsSum) + (0.0000023 * skinfoldsSum ** 2) - (0.0001392 * age);
      break;
    case '2':
    case '3':
      density = gender === 'male'
        ? 1.112 - (0.00043499 * skinfoldsSum) + (0.00000055 * skinfoldsSum ** 2) - (0.00028826 * age)
        : 1.097 - (0.00046971 * skinfoldsSum) + (0.00000056 * skinfoldsSum ** 2) - (0.00012828 * age);
      break;
    case '4':
      density = gender === 'male'
        ? 1.17136 - (0.06706 * Math.log10(skinfoldsSum))
        : 1.1665 - (0.07063 * Math.log10(skinfoldsSum));
      break;
    default:
      return 'Undefined';
  }

  const fatPercentage = (495 / density) - 450;
  return fatPercentage.toFixed(2);
};

const calculateIdealBodyFatPercentage = (age, gender) => {
  if (gender === 'male') {
    if (age < 30) return { min: 8, max: 15 };
    if (age < 50) return { min: 11, max: 17 };
    return { min: 12, max: 19 };
  } else {
    if (age < 30) return { min: 15, max: 23 };
    if (age < 50) return { min: 18, max: 25 };
    return { min: 20, max: 28 };
  }
};

const calculateCompleteBodyFat = (skinfolds, equation, gender, age) => {
  const sum = parseFloat(calculateSkinfoldsSum(skinfolds, equation));
  const percentage = parseFloat(calculateBodyFatPercentage(gender, sum, age, equation));
  const idealRange = calculateIdealBodyFatPercentage(age, gender);
  return {
    sum,
    percentage,
    idealRange
  };
};

const calculateFatMassWeight = (weight, fatPercentage) => {
  return (weight * (fatPercentage / 100)).toFixed(2);
};

const calculateLeanBodyMass = (weight, fatMass) => {
  return (weight - fatMass).toFixed(2);
};

const calculateArmMuscleCircumference = (ac, tsf) => {
  const pi = Math.PI;
  const acNum = Number(ac);
  const tsfNum = Number(tsf) / 10;
  if (acNum <= 0 || tsfNum <= 0 || isNaN(acNum) || isNaN(tsfNum)) return 'N/A';
  const cmb = acNum - (pi * tsfNum);
  return cmb <= 0 ? 'Invalid value' : cmb.toFixed(2);
};

const calculateArmMuscleArea = (ac, tsf) => {
  const pi = Math.PI;
  const acNum = Number(ac);
  const tsfNum = Number(tsf) / 10;
  if (!acNum || !tsfNum || isNaN(acNum) || isNaN(tsfNum)) return 'N/A';
  const cmb = acNum - (pi * tsfNum);
  if (cmb <= 0) return 'Invalid value';
  const ama = (cmb * cmb) / (4 * pi);
  return ama.toFixed(2);
};

const calculateCorrectedArmMuscleArea = (ac, tsf, gender) => {
  const pi = Math.PI;
  const acNum = Number(ac);
  const tsfNum = Number(tsf) / 10;
  if (!acNum || !tsfNum || isNaN(acNum) || isNaN(tsfNum)) return 'N/A';
  const base = acNum - (pi * tsfNum);
  const corrected = (base * base) / (4 * pi);
  return gender === 'male' ? (corrected - 10).toFixed(2) : (corrected - 6.5).toFixed(2);
};

const calculateArmArea = (ac) => {
  const pi = Math.PI;
  const acNum = Number(ac);
  if (!acNum || acNum <= 0 || isNaN(acNum)) return 'N/A';
  const aa = (pi * Math.pow(acNum, 2)) / 4;
  return aa.toFixed(2);
};

const calculateArmFatArea = (aa, ama) => {
  return (aa - ama).toFixed(2);
};

const classifyMuscleMass = (ama, gender) => {
  if (gender === 'male') {
    if (ama <= 35) return 'Low muscle mass';
    if (ama > 35 && ama <= 45) return 'Below average';
    if (ama > 45 && ama <= 60) return 'Average';
    if (ama > 60 && ama <= 75) return 'Above average';
    if (ama > 75) return 'High muscle mass';
  } else {
    if (ama <= 20) return 'Low muscle mass';
    if (ama > 20 && ama <= 25) return 'Below average';
    if (ama > 25 && ama <= 35) return 'Average';
    if (ama > 35 && ama <= 55) return 'Above average';
    if (ama > 55) return 'High muscle mass';
  }
  return 'Undetermined';
};

const calculateMuscleMassDiagnosis = (bmi, fatPercentage, muscleClass) => {
  if (bmi > 30) return 'Obesity';
  if (fatPercentage > 25) return 'Excess fat';
  if (muscleClass === 'Below average') return 'Low muscle mass';
  return 'Normal';
};

const calculateTotalBodyWater = (leanMass) => {
  return (leanMass * 0.73).toFixed(2);
};

export {
  calculateSkinfoldsSum,
  calculateBodyFatPercentage,
  calculateIdealBodyFatPercentage,
  calculateCompleteBodyFat,
  calculateFatMassWeight,
  calculateLeanBodyMass,
  calculateArmMuscleCircumference,
  calculateArmMuscleArea,
  calculateCorrectedArmMuscleArea,
  calculateArmArea,
  calculateArmFatArea,
  classifyMuscleMass,
  calculateMuscleMassDiagnosis,
  calculateTotalBodyWater
};